import { useState, useEffect, useMemo } from "react";
import { ListaClientesPorEjecutivo, Header, AgenciasDropdown, CategoriaSubcategoriaFilter } from "../../components";
import { useAuthContext } from "../../context/auth";
import { getClientesPublicitarios, getCategorias, getUsers, getIntermediarios, getSubcategorias } from "../../utils";
import styles from "./ClientesCartera.module.scss";

export const ClientesCartera = () => {
  const { pais } = useAuthContext();
  const [clientes, setClientes] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [intermediarios, setIntermediarios] = useState([]);
  const [subcategorias, setSubcategorias] = useState([]);

  // Estado de filtros — single source of truth en el page (paridad con VentasDetalle).
  const [busqueda, setBusqueda] = useState("");
  const [filtroAgencia, setFiltroAgencia] = useState("");
  const [filtroCategoria, setFiltroCategoria] = useState("");
  const [filtroSubcategoria, setFiltroSubcategoria] = useState("");
  const [filtroTipo, setFiltroTipo] = useState("");
  const [filtroEjecutivo, setFiltroEjecutivo] = useState("");
  const [filtroBroker, setFiltroBroker] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      // includeDisabled: true → no perdemos el nombre si el cliente está asignado
      // a un ejecutivo deshabilitado.
      const [clientesData, categoriasData, usuariosData, intermediariosData, subcategoriasData] = await Promise.all([
        getClientesPublicitarios({ pais }),
        getCategorias(),
        getUsers({ includeDisabled: true }),
        getIntermediarios({ pais }),
        getSubcategorias(),
      ]);
      setClientes(clientesData || []);
      setCategorias(categoriasData || []);
      setUsuarios(usuariosData || []);
      setIntermediarios(intermediariosData || []);
      setSubcategorias(subcategoriasData || []);
    };
    fetchData();
  }, [pais]);

  // Lookups por ID (regla 10.7): nunca por nombre.
  const usuariosById = useMemo(() => new Map(usuarios.map((u) => [Number(u.id), u])), [usuarios]);
  const intermediariosById = useMemo(() => new Map(intermediarios.map((i) => [Number(i.id), i])), [intermediarios]);
  const categoriasById = useMemo(() => new Map(categorias.map((c) => [Number(c.id), c])), [categorias]);

  // Filas resueltas (nombre↔id) — fuente única para tabla y buscador.
  const filas = useMemo(
    () =>
      clientes.map((c) => ({
        id: c.id,
        cliente: c.nombre || "",
        categoriaId: c.categoria,
        categoria: categoriasById.get(Number(c.categoria))?.nombre || "",
        subcategoriaId: c.subcategoria_id,
        tipo: c.categoria_cliente || null,
        ejecutivoId: c.ejecutivo,
        ejecutivo: usuariosById.get(Number(c.ejecutivo))?.nombre || "",
        agenciaId: c.agencia,
        agencia: intermediariosById.get(Number(c.agencia))?.nombre || "",
        brokerId: c.broker_id,
        broker: c.broker_id != null ? intermediariosById.get(Number(c.broker_id))?.nombre || "" : "",
      })),
    [clientes, categoriasById, usuariosById, intermediariosById]
  );

  // Brokers activos del país: opciones del select + columna condicional.
  const brokersActivos = useMemo(
    () =>
      [...new Set(clientes.map((c) => c.broker_id).filter((id) => id != null))]
        .map((id) => {
          const inter = intermediariosById.get(Number(id));
          return inter ? { id: Number(id), nombre: inter.nombre } : null;
        })
        .filter(Boolean)
        .sort((a, b) => a.nombre.localeCompare(b.nombre)),
    [clientes, intermediariosById]
  );
  const hayBrokers = brokersActivos.length > 0;

  // Ejecutivos con clientes en el país (opciones del select Ejecutivo).
  const ejecutivosConClientes = useMemo(() => {
    const ids = new Set(clientes.map((c) => Number(c.ejecutivo)).filter((id) => !Number.isNaN(id)));
    return [...ids]
      .map((id) => usuariosById.get(id))
      .filter(Boolean)
      .sort((a, b) => (a.nombre || "").localeCompare(b.nombre || ""));
  }, [clientes, usuariosById]);

  const filtros = {
    busqueda,
    filtroAgencia,
    filtroCategoria,
    filtroSubcategoria,
    filtroTipo,
    filtroEjecutivo,
    filtroBroker,
  };

  return (
    <div className={styles.page}>
      <Header expandChildren>
        {/* Mismo esqueleto que VentasDetalle: wrapper flex space-between + filtersContainer (flex:1). */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
            gap: "16px",
          }}
        >
          <div className={styles.filtersContainer} style={{ flex: 1 }}>
            {/* Fila 1 (3): Buscador, Categoría, Subcategoría */}
            <div className={styles.filterRow}>
              <div>
                <input
                  type="text"
                  className={`form-control ${busqueda ? styles.activeFilter : ""}`}
                  placeholder="Buscar…"
                  value={busqueda}
                  onChange={(e) => setBusqueda(e.target.value)}
                />
              </div>
              <CategoriaSubcategoriaFilter
                categorias={categorias}
                subcategorias={subcategorias}
                filtroCategoria={filtroCategoria}
                filtroSubcategoria={filtroSubcategoria}
                onCategoriaChange={setFiltroCategoria}
                onSubcategoriaChange={setFiltroSubcategoria}
                className="form-control"
                activeClass={styles.activeFilter}
              />
            </div>
            {/* Fila 2 (3): Agencia, Tipo, Ejecutivo (+ Broker si el país tiene brokers) */}
            <div className={styles.filterRow}>
              <div>
                <AgenciasDropdown
                  value={filtroAgencia}
                  onChange={(e) => setFiltroAgencia(e.target.value)}
                  intermediarios={intermediarios}
                  placeholder="Agencia"
                  className={`form-control ${filtroAgencia ? styles.activeFilter : ""}`}
                />
              </div>
              <div>
                <select
                  className={`form-control ${filtroTipo ? styles.activeFilter : ""}`}
                  value={filtroTipo}
                  onChange={(e) => setFiltroTipo(e.target.value)}
                >
                  <option value="">Tipo</option>
                  {["A", "B", "C"].map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <select
                  className={`form-control ${filtroEjecutivo ? styles.activeFilter : ""}`}
                  value={filtroEjecutivo}
                  onChange={(e) => setFiltroEjecutivo(e.target.value)}
                >
                  <option value="">Ejecutivo</option>
                  {ejecutivosConClientes.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.nombre}
                    </option>
                  ))}
                </select>
              </div>
              {hayBrokers && (
                <div>
                  <select
                    className={`form-control ${filtroBroker ? styles.activeFilter : ""}`}
                    value={filtroBroker}
                    onChange={(e) => setFiltroBroker(e.target.value)}
                  >
                    <option value="">Todos</option>
                    <option value="grupo:visiona">Visiona</option>
                    <option value="grupo:adlab">AdLab</option>
                  </select>
                </div>
              )}
            </div>
          </div>
        </div>
      </Header>

      <div className={styles.content}>
        <ListaClientesPorEjecutivo filas={filas} hayBrokers={hayBrokers} filtros={filtros} />
      </div>
    </div>
  );
};
